/**
 * Composant de détail d'un parcours pour l'admin
 * Affiche les informations complètes d'un parcours et ses évaluations
 */

import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  Progress,
} from "@repo/ui";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  CheckCircle,
  Eye,
  ExternalLink,
  FileText,
  Inbox,
  KeyRound,
  Loader2,
  Mail,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { buildParcoursDescription } from "@repo/utils";
import {
  StatutGlobalEnum,
  TypeEvalEnum,
  estEvaluationSoumise,
} from "../../../../convex/schema";

type EmailStatus = "idle" | "sending" | "success" | "error";

export function ParcoursDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backPath = searchParams.get("from") ?? "/";

  // State pour la modale d'envoi de lien
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatus>("idle");
  const [emailMessage, setEmailMessage] = useState("");

  // State pour la modale d'envoi des résultats
  const [resultatsModalOpen, setResultatsModalOpen] = useState(false);
  const [resultatsStatus, setResultatsStatus] = useState<EmailStatus>("idle");
  const [resultatsMessage, setResultatsMessage] = useState("");

  // State pour la modale d'aperçu email
  const [aperçuModalOpen, setAperçuModalOpen] = useState(false);
  const [aperçuHtml, setAperçuHtml] = useState("");
  const [aperçuLoading, setAperçuLoading] = useState(false);

  // State pour la relance analyse inbasket
  const [relancingId, setRelancingId] = useState<string | null>(null);

  // Mutations
  const archiverParcours = useMutation(
    api.parcoursEvaluations.archiverParcours,
  );
  const relancerAnalyseInbasket = useMutation(
    api.evaluations.relancerAnalyseInbasket,
  );
  const desarchiverParcours = useMutation(
    api.parcoursEvaluations.desarchiverParcours,
  );

  // Actions
  const envoyerLienParcours = useAction(api.email.envoyerLienParcours);
  const envoyerResultatsParcours = useAction(
    api.email.envoyerResultatsParcours,
  );
  const genererApercuResultats = useAction(api.email.genererApercuResultats);

  // Query
  const result = useQuery(
    api.parcoursEvaluations.obtenirDetailParcoursAdmin,
    id ? { parcoursId: id as Id<"parcoursEvaluations"> } : "skip",
  );

  // Handlers
  const handleArchive = async () => {
    if (id) {
      await archiverParcours({ parcoursId: id as Id<"parcoursEvaluations"> });
    }
  };

  const handleDesarchive = async () => {
    if (id) {
      await desarchiverParcours({
        parcoursId: id as Id<"parcoursEvaluations">,
      });
    }
  };

  // Ouvrir la modale de confirmation
  const openEmailModal = () => {
    setEmailStatus("idle");
    setEmailMessage("");
    setEmailModalOpen(true);
  };

  // Envoyer l'email
  const handleSendEmail = async () => {
    if (!id || !result || !("parcours" in result)) return;

    setEmailStatus("sending");
    setEmailMessage("Envoi en cours...");

    try {
      const emailResult = await envoyerLienParcours({
        parcoursId: id as Id<"parcoursEvaluations">,
      });

      if (emailResult.succes) {
        setEmailStatus("success");
        setEmailMessage(
          `L'email a été envoyé avec succès à ${result.parcours.emailCandidat}`,
        );
      } else {
        setEmailStatus("error");
        setEmailMessage(
          emailResult.message ||
            "Une erreur est survenue lors de l'envoi de l'email.",
        );
      }
    } catch (error) {
      setEmailStatus("error");
      setEmailMessage(
        error instanceof Error
          ? error.message
          : "Une erreur inattendue est survenue.",
      );
    }
  };

  // Fermer la modale lien
  const closeEmailModal = () => {
    setEmailModalOpen(false);
    setEmailStatus("idle");
    setEmailMessage("");
  };

  // Envoyer les résultats
  const handleSendResultats = async () => {
    if (!id) return;
    setResultatsStatus("sending");
    setResultatsMessage("Envoi en cours...");
    try {
      const res = await envoyerResultatsParcours({
        parcoursId: id as Id<"parcoursEvaluations">,
      });
      if (res.succes) {
        setResultatsStatus("success");
        setResultatsMessage(res.message);
      } else {
        setResultatsStatus("error");
        setResultatsMessage(res.message || "Une erreur est survenue.");
      }
    } catch (error) {
      setResultatsStatus("error");
      setResultatsMessage(
        error instanceof Error
          ? error.message
          : "Une erreur inattendue est survenue.",
      );
    }
  };

  const closeResultatsModal = () => {
    setResultatsModalOpen(false);
    setResultatsStatus("idle");
    setResultatsMessage("");
  };

  const handleAperçuResultats = async () => {
    if (!id) return;
    setAperçuLoading(true);
    setAperçuHtml("");
    setAperçuModalOpen(true);
    try {
      const res = await genererApercuResultats({
        parcoursId: id as Id<"parcoursEvaluations">,
      });
      if (res.succes) {
        setAperçuHtml(res.html);
      } else {
        setAperçuHtml(`<p style="color:red;padding:16px;">${res.message}</p>`);
      }
    } catch (error) {
      setAperçuHtml(
        `<p style="color:red;padding:16px;">${error instanceof Error ? error.message : "Erreur inconnue"}</p>`,
      );
    } finally {
      setAperçuLoading(false);
    }
  };

  const handleRelancerInbasket = async (evaluationId: string) => {
    setRelancingId(evaluationId);
    try {
      await relancerAnalyseInbasket({
        evaluationId: evaluationId as Id<"evaluations">,
      });
    } finally {
      setRelancingId(null);
    }
  };

  // Formatage de la date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Badge de statut
  const getStatusBadge = (statut: StatutGlobalEnum) => {
    if (statut === StatutGlobalEnum.ANALYSE_EN_COURS)
      return <Badge variant="warning">Analyse en cours</Badge>;
    if (statut === StatutGlobalEnum.ERREUR_ANALYSE)
      return <Badge variant="destructive">Erreur d'analyse</Badge>;
    if (estEvaluationSoumise(statut))
      return <Badge variant="success">Terminée</Badge>;
    if (statut === StatutGlobalEnum.EN_COURS)
      return <Badge variant="warning">En cours</Badge>;
    return <Badge variant="purple">À faire</Badge>;
  };

  // Icône d'évaluation
  const getEvaluationIcon = (typeEval: string) => {
    switch (typeEval) {
      case TypeEvalEnum.FORM:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
      case TypeEvalEnum.INBASKET:
        return <Inbox className="h-5 w-5 text-muted-foreground" />;
      case TypeEvalEnum.ENT_STRUCT:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
      default:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  // Titre de l'évaluation
  const getEvaluationTitle = (evaluation: {
    typeEval: string;
    template?: string;
  }) => {
    if (evaluation.typeEval === TypeEvalEnum.FORM) {
      switch (evaluation.template) {
        case "autodiag":
          return "Autodiagnostic";
        case "egogramme":
          return "Egogramme";
        case "pami":
          return "PAMI";
        default:
          return "Questionnaire";
      }
    }
    if (evaluation.typeEval === TypeEvalEnum.ENT_STRUCT) {
      return "Entretien structuré";
    }
    return "Évaluation In-Basket";
  };

  // Description de l'évaluation
  const getEvaluationDescription = (evaluation: {
    typeEval: string;
    template?: string;
  }) => {
    if (evaluation.typeEval === TypeEvalEnum.FORM) {
      switch (evaluation.template) {
        case "autodiag":
          return "Questionnaire d'auto-évaluation des compétences";
        case "egogramme":
          return "Questionnaire sur le style relationnel";
        case "pami":
          return "Questionnaire de personnalité";
        default:
          return "Questionnaire à compléter";
      }
    }
    if (evaluation.typeEval === TypeEvalEnum.ENT_STRUCT) {
      return evaluation.template || "Template non défini";
    }
    return "Gestion de boîte de réception simulée";
  };

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (result === null) {
    return (
      <div className="w-full min-w-0 p-4 md:p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">
            Parcours introuvable
          </h1>
          <p className="mt-2 text-muted-foreground">
            Le parcours demandé n'existe pas.
          </p>
          <button onClick={() => navigate(backPath)} className="mt-4 inline-block">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
          </button>
        </div>
      </div>
    );
  }

  const { parcours, evaluations, progression } = result;

  const hasAutodiag = evaluations.some(
    (e) => e.typeEval === TypeEvalEnum.FORM && e.template === "autodiag",
  );
  const hasInbasket = evaluations.some(
    (e) => e.typeEval === TypeEvalEnum.INBASKET,
  );
  const isCadreSante = parcours.typeParcours === "recrutement_cadre_de_sante";
  const allTermine = evaluations.every(
    (e) => e.statut === StatutGlobalEnum.TERMINE,
  );
  const peutEnvoyerResultats = isCadreSante
    ? allTermine
    : hasAutodiag && hasInbasket && progression.pourcentage === 100;
  const peutAperçuResultats = isCadreSante && progression.pourcentage === 100;

  return (
    <div className="w-full min-w-0 p-4 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(backPath)}
          className="text-sm text-muted-foreground hover:underline"
        >
          <ArrowLeft className="h-4 w-4 inline mr-1" />
          Retour
        </button>
      </div>

      {/* Parcours Info */}
      <Card className="mb-6 w-full">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl">
                {parcours.prenomCandidat} {parcours.nomCandidat}
              </CardTitle>
              <CardDescription className="mt-1">
                {parcours.emailCandidat}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  window.open(
                    `${process.env.BUN_PUBLIC_APP_CANDIDATE_PORTAIL_URL}/p/${id}`,
                    "_blank",
                  )
                }
                title="Voir le portail candidat"
              >
                <KeyRound className="h-4 w-4 mr-2" />
                Portail candidat
              </Button>
              <Button variant="outline" onClick={openEmailModal}>
                <Mail className="h-4 w-4 mr-2" />
                Envoyer le lien
              </Button>
              {peutAperçuResultats && (
                <Button variant="outline" onClick={handleAperçuResultats}>
                  <Eye className="h-4 w-4 mr-2" />
                  Aperçu email résultats
                </Button>
              )}
              {peutEnvoyerResultats && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setResultatsStatus("idle");
                    setResultatsMessage("");
                    setResultatsModalOpen(true);
                  }}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer les résultats
                </Button>
              )}
              {parcours.archive ? (
                <Button variant="outline" onClick={handleDesarchive}>
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                  Désarchiver
                </Button>
              ) : (
                <Button variant="outline" onClick={handleArchive}>
                  <Archive className="h-4 w-4 mr-2" />
                  Archiver
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Type de parcours</span>
              <p className="font-medium">{parcours.typeParcours}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Titre</span>
              <p className="font-medium">{parcours.titre}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Date de création</span>
              <p className="font-medium">{formatDate(parcours.dateCreation)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">
                Dernière modification
              </span>
              <p className="font-medium">
                {formatDate(parcours.dateModification)}
              </p>
            </div>
          </div>

          <div className="pt-2 border-t">
            <span className="text-muted-foreground text-sm">Description</span>
            <p className="mt-1">
              {buildParcoursDescription(
                evaluations.map((e) => ({
                  typeEval: e.typeEval,
                  template: e.template,
                })),
              )}
            </p>
          </div>

          {parcours.archive && (
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-sm text-muted-foreground">
                ⚠️ Ce parcours est archivé. Le candidat n'y a plus accès.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evaluations List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Évaluations</CardTitle>
              <CardDescription>
                Liste des évaluations du parcours
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 min-w-[140px]">
              <Progress
                value={progression.pourcentage}
                className={`h-2 flex-1 ${progression.pourcentage === 100 ? "[&>div]:bg-green-500" : ""}`}
              />
              <span
                className={`text-sm font-medium whitespace-nowrap ${progression.pourcentage === 100 ? "text-green-600" : ""}`}
              >
                {progression.pourcentage}%
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {evaluations.map((evaluation) => (
              <div
                key={evaluation.evalId}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    {getEvaluationIcon(evaluation.typeEval)}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {getEvaluationTitle(evaluation)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {getEvaluationDescription(evaluation)}
                    </p>
                    {evaluation.dateDebut && (
                      <p className="text-xs text-gray-400 mt-1">
                        Commencé le {formatDate(evaluation.dateDebut)}
                        {evaluation.dateFin &&
                          ` - Terminé le ${formatDate(evaluation.dateFin)}`}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-end gap-1">
                    {getStatusBadge(evaluation.statut)}
                  </div>
                  {evaluation.statut === StatutGlobalEnum.TERMINE && (
                    <>
                      {evaluation.typeEval === TypeEvalEnum.INBASKET && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleRelancerInbasket(evaluation.evalId)
                          }
                          disabled={relancingId === evaluation.evalId}
                        >
                          {relancingId === evaluation.evalId ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-1" />
                          )}
                          Relancer l'analyse
                        </Button>
                      )}
                      <Link to={`/p/${id}/evaluation/${evaluation.evalId}`}>
                        <Button variant="outline" size="sm">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Voir les résultats
                        </Button>
                      </Link>
                    </>
                  )}
                  {evaluation.typeEval === TypeEvalEnum.INBASKET &&
                    evaluation.statut !== StatutGlobalEnum.TERMINE && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleRelancerInbasket(evaluation.evalId)
                        }
                        disabled={relancingId === evaluation.evalId}
                        title="Déclencher l'analyse LLM même si l'évaluation n'est pas terminée"
                      >
                        {relancingId === evaluation.evalId ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4 mr-1" />
                        )}
                        Lancer l'analyse
                      </Button>
                    )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Modale d'envoi des résultats */}
      <Dialog
        isOpen={resultatsModalOpen}
        onClose={closeResultatsModal}
        title="Envoyer les résultats"
        className="max-w-md"
      >
        <div className="space-y-4">
          {resultatsStatus === "idle" && (
            <p className="text-sm text-muted-foreground">
              Les résultats de{" "}
              <strong>
                {evaluations
                  .map((e) => getEvaluationTitle(e))
                  .reduce<React.ReactNode[]>((acc, title, i, arr) => {
                    if (i === 0) return [title];
                    if (i === arr.length - 1) return [...acc, " et ", title];
                    return [...acc, ", ", title];
                  }, [])}
              </strong>{" "}
              seront envoyés à <strong>{parcours.emailCandidat}</strong>.
            </p>
          )}
          {resultatsStatus === "sending" && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">{resultatsMessage}</span>
            </div>
          )}
          {resultatsStatus === "success" && (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <p className="mt-4 text-center text-sm text-muted-foreground">
                {resultatsMessage}
              </p>
            </div>
          )}
          {resultatsStatus === "error" && (
            <div className="flex flex-col items-center justify-center py-8">
              <XCircle className="h-12 w-12 text-red-500" />
              <p className="mt-4 text-center text-sm text-muted-foreground">
                {resultatsMessage}
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            {resultatsStatus === "idle" && (
              <>
                <Button variant="outline" onClick={closeResultatsModal}>
                  Annuler
                </Button>
                <Button onClick={handleSendResultats}>
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer
                </Button>
              </>
            )}
            {(resultatsStatus === "success" || resultatsStatus === "error") && (
              <Button onClick={closeResultatsModal}>Fermer</Button>
            )}
          </div>
        </div>
      </Dialog>

      {/* Modale d'aperçu email résultats */}
      <Dialog
        isOpen={aperçuModalOpen}
        onClose={() => setAperçuModalOpen(false)}
        title="Aperçu de l'email résultats"
        className="max-w-4xl"
      >
        <div className="flex flex-col gap-4">
          {aperçuLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-3 text-sm text-muted-foreground">
                Génération de l'aperçu…
              </span>
            </div>
          ) : (
            <iframe
              srcDoc={aperçuHtml}
              className="w-full rounded border"
              style={{ height: "70vh" }}
              sandbox="allow-same-origin"
              title="Aperçu email résultats"
            />
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setAperçuModalOpen(false)}>
              Fermer
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Modale d'envoi d'email */}
      <Dialog
        isOpen={emailModalOpen}
        onClose={closeEmailModal}
        title="Envoyer le lien du parcours"
        className="max-w-md"
      >
        <div className="space-y-4">
          {emailStatus === "idle" && result && (
            <p className="text-sm text-muted-foreground">
              Un email sera envoyé à{" "}
              <strong>{result.parcours.emailCandidat}</strong> avec le lien
              d'accès au parcours.
            </p>
          )}

          {emailStatus === "sending" && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">{emailMessage}</span>
            </div>
          )}

          {emailStatus === "success" && (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <p className="mt-4 text-center text-sm text-muted-foreground">
                {emailMessage}
              </p>
            </div>
          )}

          {emailStatus === "error" && (
            <div className="flex flex-col items-center justify-center py-8">
              <XCircle className="h-12 w-12 text-red-500" />
              <p className="mt-4 text-center text-sm text-muted-foreground">
                {emailMessage}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            {emailStatus === "idle" && (
              <>
                <Button variant="outline" onClick={closeEmailModal}>
                  Annuler
                </Button>
                <Button onClick={handleSendEmail}>
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer
                </Button>
              </>
            )}
            {(emailStatus === "success" || emailStatus === "error") && (
              <Button onClick={closeEmailModal}>Fermer</Button>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}
